/**
 * @name HomePageEvolutionChart
 * @description Componente para exibir gráfico de evolução de gastos usando Material-UI
 * @author Gabriel M. Guimarães | gabrielmg7
 */

import { BarChart as BarChartIcon, Refresh as RefreshIcon, TrendingDown, TrendingUp } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import React from 'react';
import { useThemeContext } from '../../../contexts';

interface IEvolucaoGastos {
     mes: string;
     total_gastos: number;
}

interface HomePageEvolutionChartProps {
     data: IEvolucaoGastos[];
     title: string;
     onRefresh?: () => void;
}

export const HomePageEvolutionChart: React.FC<HomePageEvolutionChartProps> = ({ data, title, onRefresh }) => {
     const { theme } = useThemeContext();

     const formatCurrency = (value: number) => {
          return new Intl.NumberFormat('pt-BR', {
               style: 'currency',
               currency: 'BRL',
          }).format(value);
     };

     const formatMonth = (monthString: string) => {
          try {
               const [year, month] = monthString.split('-');
               const date = new Date(parseInt(year), parseInt(month) - 1);
               return date.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
          } catch {
               return monthString;
          }
     };

     return (
          <Card sx={{ height: '100%' }}>
               <CardContent sx={{ p: 3 }}>
                    <Typography
                         variant='h6'
                         gutterBottom
                         fontWeight='bold'
                    >
                         {title}
                    </Typography>

                    {data.length === 0 ? (
                         <Box
                              sx={{
                                   height: 300,
                                   display: 'flex',
                                   flexDirection: 'column',
                                   alignItems: 'center',
                                   justifyContent: 'center',
                                   gap: 2,
                                   backgroundColor: theme.palette.grey[50],
                                   borderRadius: 2,
                                   border: `2px dashed ${theme.palette.grey[300]}`,
                                   mx: 1,
                              }}
                         >
                              <BarChartIcon
                                   sx={{
                                        fontSize: 64,
                                        color: theme.palette.grey[400],
                                        mb: 1,
                                   }}
                              />
                              <Typography
                                   variant='h6'
                                   color='text.secondary'
                                   sx={{ fontWeight: 500 }}
                              >
                                   Nenhum dado disponível
                              </Typography>
                              <Typography
                                   variant='body2'
                                   color='text.secondary'
                                   textAlign='center'
                                   sx={{ maxWidth: 280 }}
                              >
                                   Não há dados de gastos para exibir no período selecionado. Os dados aparecerão aqui conforme as transações forem realizadas.
                              </Typography>

                              {onRefresh && (
                                   <Button
                                        variant='outlined'
                                        startIcon={<RefreshIcon />}
                                        onClick={onRefresh}
                                        sx={{
                                             mt: 1,
                                             color: theme.palette.primary.main,
                                             borderColor: theme.palette.primary.main,
                                             '&:hover': {
                                                  backgroundColor: theme.palette.primary.main,
                                                  color: 'white',
                                             },
                                        }}
                                   >
                                        Atualizar Dados
                                   </Button>
                              )}
                         </Box>
                    ) : (
                         <Box sx={{ height: 300, width: '100%' }}>
                              <BarChart
                                   xAxis={[
                                        {
                                             data: data.map((item) => formatMonth(item.mes)),
                                             scaleType: 'band',
                                             label: 'Mês',
                                        },
                                   ]}
                                   series={[
                                        {
                                             data: data.map((item) => item.total_gastos),
                                             label: 'Gastos',
                                             color: theme.palette.primary.main,
                                        },
                                   ]}
                                   height={250}
                                   margin={{ top: 20, right: 20, left: 20, bottom: 40 }}
                                   slotProps={{
                                        legend: { hidden: true },
                                   }}
                                   yAxis={[
                                        {
                                             label: 'Total (R$)',
                                             valueFormatter: (value: number) => formatCurrency(value),
                                        },
                                   ]}
                              />
                         </Box>
                    )}

                    {data.length > 0 && (
                         <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography
                                   variant='caption'
                                   color='text.secondary'
                              >
                                   Período: {formatMonth(data[0].mes)} - {formatMonth(data[data.length - 1].mes)}
                              </Typography>

                              {data.length >= 2 && (
                                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {data[data.length - 1].total_gastos > data[data.length - 2].total_gastos ? <TrendingUp sx={{ color: theme.palette.error.main, fontSize: 16 }} /> : <TrendingDown sx={{ color: theme.palette.success.main, fontSize: 16 }} />}
                                        <Typography
                                             variant='caption'
                                             color='text.secondary'
                                        >
                                             {data[data.length - 1].total_gastos > data[data.length - 2].total_gastos ? 'Crescimento' : 'Redução'}
                                        </Typography>
                                   </Box>
                              )}
                         </Box>
                    )}
               </CardContent>
          </Card>
     );
};
